import {
  METRICS_INTERVAL_CONFIG,
  SCORING_SERIES,
  ScoringSeriesKey,
  MetricsInterval,
} from './constants';
import { assignSeriesColors } from './seriesColors';
import {
  MetricsChartData,
  MetricsNullableNumber,
  MetricsSeriesStats,
  ScoringHealthInstance,
  ScoringHealthResponse,
  ScoringHealthSeries,
} from './types';

const SCORING_SERIES_COLORS = Object.fromEntries(
  SCORING_SERIES.map((series) => [series.key, series.color])
);

type SeriesValues = Record<ScoringSeriesKey, MetricsNullableNumber[]>;

const JAMES_UNIFIED_1: SeriesValues = {
  started: [
    null,
    6,
    1,
    14,
    6,
    5,
    7,
    15,
    3,
    15,
    9,
    6,
    6,
    11,
    6,
    5,
    2,
    8,
    4,
    5,
    1,
  ],
  success: [
    null,
    6,
    1,
    14,
    6,
    5,
    7,
    15,
    3,
    15,
    9,
    6,
    6,
    11,
    6,
    5,
    2,
    8,
    4,
    5,
    0,
  ],
  errors: [null, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  aborted: [null, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  slow: [null, 0, 1, 0, 0, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  retries: [null, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  degraded: [null, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  droppedCalls: [
    null,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
  ],
};

const JAMES_UNIFIED_2: SeriesValues = {
  started: [
    null,
    7,
    1,
    13,
    15,
    5,
    8,
    7,
    3,
    11,
    2,
    7,
    2,
    14,
    11,
    9,
    7,
    11,
    5,
    4,
    5,
  ],
  success: [
    null,
    7,
    1,
    13,
    15,
    5,
    8,
    7,
    3,
    11,
    2,
    7,
    2,
    14,
    11,
    9,
    7,
    11,
    5,
    4,
    4,
  ],
  errors: [null, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  aborted: [null, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  slow: [null, 0, 0, 0, 0, 0, 1, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  retries: [null, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 18, 0, 0],
  degraded: [null, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
  droppedCalls: [
    null,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
    0,
  ],
};

const EMPTY_SERIES: SeriesValues = {
  started: [],
  success: [],
  errors: [],
  aborted: [],
  slow: [],
  retries: [],
  degraded: [],
  droppedCalls: [],
};

const INSTANCE_TEMPLATES: {
  targetId: string;
  name: string;
  status: string;
  idle?: boolean;
  source?: SeriesValues;
}[] = [
  {
    targetId: '9',
    name: 'Spotify Unified 1',
    status: 'ok',
    source: JAMES_UNIFIED_1,
  },
  {
    targetId: '10',
    name: 'Spotify Unified 2',
    status: 'ok',
    source: JAMES_UNIFIED_2,
  },
  { targetId: '11', name: 'Spotify Unified 3', status: 'ok', idle: true },
];

const resizeValues = (
  values: MetricsNullableNumber[],
  length: number
): MetricsNullableNumber[] => {
  if (length <= 0) {
    return [];
  }
  if (!values.length) {
    return Array.from({ length }, () => null);
  }
  if (values.length === length) {
    return values;
  }
  if (values.length > length) {
    return values.slice(values.length - length);
  }

  return Array.from({ length }, (_, index) => {
    const sourceIndex = Math.floor((index * values.length) / length);
    return values[sourceIndex];
  });
};

const statsFromValues = (
  values: MetricsNullableNumber[]
): MetricsSeriesStats => {
  const numbers = values.filter((value): value is number => value !== null);

  if (!numbers.length) {
    return { mean: null, last: null, max: null, total: 0 };
  }

  const total = numbers.reduce((sum, value) => sum + value, 0);

  return {
    mean: total / numbers.length,
    last: numbers[numbers.length - 1],
    max: Math.max(...numbers),
    total,
  };
};

const buildSeries = (
  source: SeriesValues,
  length: number
): ScoringHealthSeries[] =>
  SCORING_SERIES.map((meta) => {
    const resized = resizeValues(source[meta.key] || [], length);

    return {
      key: meta.key,
      name: meta.name,
      values: resized,
      stats: statsFromValues(resized),
    };
  });

const buildIdleSeries = (length: number): ScoringHealthSeries[] =>
  SCORING_SERIES.map((meta) => {
    const values = Array.from({ length }, () => null);
    return {
      key: meta.key,
      name: meta.name,
      values,
      stats: statsFromValues(values),
    };
  });

const buildTimeline = (interval: MetricsInterval) => {
  const { lookbackMs, periodMs } = METRICS_INTERVAL_CONFIG[interval];
  const pointCount = Math.round(lookbackMs / periodMs) + 1;
  const end = Date.now();
  const timestamps = Array.from(
    { length: pointCount },
    (_, index) => end - (pointCount - 1 - index) * periodMs
  );

  return {
    timestamps,
    stepSeconds: periodMs / 1000,
    from: new Date(timestamps[0]).toISOString(),
    to: new Date(timestamps[timestamps.length - 1]).toISOString(),
  };
};

export const getMockScoringHealth = (
  interval: MetricsInterval
): ScoringHealthResponse => {
  const { timestamps, stepSeconds, from, to } = buildTimeline(interval);

  const instances: ScoringHealthInstance[] = INSTANCE_TEMPLATES.map(
    (template) => ({
      targetId: template.targetId,
      name: template.name,
      status: template.status,
      series: template.idle
        ? buildIdleSeries(timestamps.length)
        : buildSeries(template.source || EMPTY_SERIES, timestamps.length),
    })
  );

  return {
    from,
    to,
    stepSeconds,
    timestamps,
    downsampled: false,
    instances,
  };
};

export const scoringInstanceToChart = (
  instance: ScoringHealthInstance,
  timestamps: number[],
  interval: MetricsInterval,
  stepSeconds?: number
): MetricsChartData => {
  const colors = assignSeriesColors(instance.series, SCORING_SERIES_COLORS);

  return {
    id: instance.targetId,
    title: `${instance.name} (${instance.status})`,
    interval,
    timestamps,
    stepMs: stepSeconds ? stepSeconds * 1000 : undefined,
    series: instance.series.map((item, index) => ({
      key: item.key,
      name: item.name,
      color: colors[index],
      values: item.values,
      stats: item.stats,
    })),
  };
};
